/*
  SocialIcons.js — reusable social/contact icon rows, plus an
  expandable accordion toggle for secondary text links (currently
  "Who Are We", extensible for more later).

  Two distinct sets by visibility/purpose (director's call):
  - TOP_CHROME_LINKS: Substack, Bluesky, Pinterest, Email - persistent,
    high-visibility row, favors direct/functional channels.
  - ABOUT_LINKS: Facebook, Instagram, X - lower-visibility row inside
    AboutSection.js, broader/passive social footprint.

  All hrefs are placeholders ("#") until director provides real URLs,
  except Email which is a real mailto: link.

  Usage:
    renderSocialIcons({ variant: 'landing' })  -> top-chrome set + accordion
    renderSocialIcons({ variant: 'about' })    -> About section set, no accordion
*/

import { showWhoAreWeModal } from './WhoAreWeModal.js';

const ICONS = {
  facebook: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12.06C22 6.53 17.52 2 12 2S2 6.53 2 12.06c0 4.99 3.66 9.13 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.1 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34V22c4.78-.81 8.44-4.95 8.44-9.94Z"/></svg>',
  instagram: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2c-2.72 0-3.06.01-4.12.06-1.06.05-1.79.22-2.43.47-.66.26-1.22.6-1.77 1.15A4.9 4.9 0 0 0 2.53 5.45c-.25.64-.42 1.37-.47 2.43C2.01 8.94 2 9.28 2 12s.01 3.06.06 4.12c.05 1.06.22 1.79.47 2.43.26.66.6 1.22 1.15 1.77.55.55 1.11.89 1.77 1.15.64.25 1.37.42 2.43.47C8.94 21.99 9.28 22 12 22s3.06-.01 4.12-.06c1.06-.05 1.79-.22 2.43-.47.66-.26 1.22-.6 1.77-1.15.55-.55.89-1.11 1.15-1.77.25-.64.42-1.37.47-2.43.05-1.06.06-1.4.06-4.12s-.01-3.06-.06-4.12c-.05-1.06-.22-1.79-.47-2.43a4.9 4.9 0 0 0-1.15-1.77 4.9 4.9 0 0 0-1.77-1.15c-.64-.25-1.37-.42-2.43-.47C15.06 2.01 14.72 2 12 2Zm0 1.8c2.67 0 2.99.01 4.04.06.98.04 1.5.21 1.86.34.47.18.8.4 1.15.75.35.35.57.68.75 1.15.13.36.3.88.34 1.86.05 1.05.06 1.37.06 4.04s-.01 2.99-.06 4.04c-.04.98-.21 1.5-.34 1.86-.18.47-.4.8-.75 1.15-.35.35-.68.57-1.15.75-.36.13-.88.3-1.86.34-1.05.05-1.37.06-4.04.06s-2.99-.01-4.04-.06c-.98-.04-1.5-.21-1.86-.34a3.1 3.1 0 0 1-1.15-.75 3.1 3.1 0 0 1-.75-1.15c-.13-.36-.3-.88-.34-1.86-.05-1.05-.06-1.37-.06-4.04s.01-2.99.06-4.04c.04-.98.21-1.5.34-1.86.18-.47.4-.8.75-1.15.35-.35.68-.57 1.15-.75.36-.13.88-.3 1.86-.34C9.01 3.81 9.33 3.8 12 3.8Zm0 3.06a5.14 5.14 0 1 0 0 10.28 5.14 5.14 0 0 0 0-10.28Zm0 8.48a3.34 3.34 0 1 1 0-6.68 3.34 3.34 0 0 1 0 6.68Zm5.34-8.68a1.2 1.2 0 1 1-2.4 0 1.2 1.2 0 0 1 2.4 0Z"/></svg>',
  pinterest: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12c0 4.24 2.64 7.86 6.36 9.32-.09-.79-.17-2.01.04-2.87.19-.79 1.22-5.01 1.22-5.01s-.31-.62-.31-1.54c0-1.44.84-2.52 1.87-2.52.88 0 1.31.66 1.31 1.45 0 .89-.56 2.21-.86 3.44-.24 1.03.52 1.87 1.53 1.87 1.84 0 3.08-2.36 3.08-5.16 0-2.13-1.43-3.72-4.04-3.72-2.94 0-4.78 2.19-4.78 4.65 0 .84.25 1.44.64 1.9.18.21.2.3.14.54-.05.18-.16.63-.2.8-.07.26-.27.35-.5.25-1.39-.57-2.04-2.09-2.04-3.8 0-2.83 2.38-6.22 7.09-6.22 3.79 0 6.28 2.74 6.28 5.68 0 3.89-2.16 6.8-5.34 6.8-1.07 0-2.07-.58-2.42-1.24 0 0-.58 2.31-.7 2.76-.21.79-.63 1.58-1.01 2.2.9.27 1.85.42 2.84.42 5.52 0 10-4.48 10-10S17.52 2 12 2Z"/></svg>',
  email: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m4 7 8 6 8-6"/></svg>',
  substack: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 4h18v2.6H3V4Zm0 4.4h18V11H3V8.4ZM3 20V12.8h18V20l-9-5.2L3 20Z"/></svg>',
  bluesky: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 8.6C10.6 5.9 7.9 3.6 5.4 3c-1.4-.3-2.1.4-1.8 1.9.3 1.7 1 5.7 1.5 7.3.6 2 2.7 2.6 4.5 2.3-3.3.5-6.2 1.8-2.4 6.1 4.2 4.6 5.8-1 5.8-1s1.6 5.6 5.8 1c3.8-4.3.9-5.6-2.4-6.1 1.8.3 3.9-.3 4.5-2.3.5-1.6 1.2-5.6 1.5-7.3.3-1.5-.4-2.2-1.8-1.9-2.5.6-5.2 2.9-6.6 5.6Z"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.9 2H22l-7.4 8.4L23 22h-6.8l-5.3-6.9L4.8 22H2l7.9-9L2 2h6.9l4.8 6.3L18.9 2Zm-1.2 18h1.9L7.3 4H5.3l12.4 16Z"/></svg>',
};

const TOP_CHROME_LINKS = {
  substack: 'https://substack.com/@stygianmyth?utm_source=share&utm_medium=android&r=8w18iv',
  bluesky: 'https://bsky.app/profile/stygianmyth.bsky.social',
  pinterest: 'https://pin.it/6XI3nqzFX',
  email: 'mailto:stygianmyth@gmail.com',
};

const ABOUT_LINKS = {
  facebook: 'https://www.facebook.com/share/194dz4eCb7/',
  instagram: 'https://www.instagram.com/stygianmyth?igsh=MXkxZXAyZW44YnRoMw==',
  x: 'https://x.com/stygianmyth',
};

const CHEVRON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>';

function getAccordionEntries() {
  return [
    { label: 'Who Are We', onClick: () => showWhoAreWeModal() },
  ];
}

function buildIconRow(links) {
  const frag = document.createDocumentFragment();
  Object.entries(links).forEach(([key, href]) => {
    const link = document.createElement('a');
    link.href = href;
    link.className = 'social-icon-link';
    link.setAttribute('aria-label', key.charAt(0).toUpperCase() + key.slice(1));
    if (key !== 'email') {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
    }
    link.innerHTML = ICONS[key];
    frag.appendChild(link);
  });
  return frag;
}

export function renderSocialIcons({ variant = 'landing' } = {}) {
  const wrapper = document.createElement('div');
  wrapper.className = `social-icons social-icons--${variant}`;
  wrapper.setAttribute('aria-label', 'Social links');

  const links = variant === 'about' ? ABOUT_LINKS : TOP_CHROME_LINKS;
  wrapper.appendChild(buildIconRow(links));

  // Accordion toggle - only on the top-chrome ('landing') variant.
  if (variant === 'landing') {
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'social-accordion-toggle';
    toggle.setAttribute('aria-label', 'More links');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.innerHTML = CHEVRON_SVG;

    const accordion = document.createElement('div');
    accordion.className = 'social-accordion';

    const inner = document.createElement('div');
    inner.className = 'social-accordion-inner';

    getAccordionEntries().forEach((entry) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'social-accordion-item';
      item.textContent = entry.label;
      item.addEventListener('click', () => entry.onClick());
      inner.appendChild(item);
    });

    accordion.appendChild(inner);

    let open = false;
    toggle.addEventListener('click', () => {
      open = !open;
      toggle.classList.toggle('social-accordion-toggle--open', open);
      toggle.setAttribute('aria-expanded', String(open));
      accordion.classList.toggle('social-accordion--open', open);
    });

    wrapper.append(toggle, accordion);
  }

  return wrapper;
}
